import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Video,
  Radio,
  PlusCircleIcon,
  MessageSquare,
  FileText,
  Loader2,
} from "lucide-react";
import { toast } from "react-toastify";
import { createReason, deleteReason } from "../../api/reasonApis";
import useSWR from "swr";
import logger from "../../utils/logger";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface StopReason {
  _id: string;
  reason: string;
  message: string;
  type: "streaming" | "recording";
  status?: "active" | "inactive";
  joinedDate: string;
  createdAt: Date | string;
}

interface UserFormData {
  type: string;
  reason: string;
  message: string;
  status: "active" | "inactive";
}

const StopReasonsManager: React.FC = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingReason, setEditingReason] = useState<StopReason | null>(null);
  const [selectedType, setSelectedType] = useState<
    "all" | "streaming" | "recording"
  >("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: reasonData, mutate } = useSWR<{ data: StopReason[] }>(
    `/api/reason/get-reason`,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UserFormData>({
    defaultValues: {
      status: "active",
    },
  });

  const onSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    try {
      let res;
      if (editingReason?._id) {
        res = await createReason({
          _id: editingReason._id,
          ...data,
          status: "active",
        });
      } else {
        res = await createReason({ ...data, status: "active" });
      }
      let resData = res?.data;
      if (resData?.status) {
        reset();
        mutate();
        toast.success(
          resData?.message ||
            (editingReason
              ? "Failed to update Reason"
              : "Failed to add Reason"),
        );
        setShowAddForm(false);
        setEditingReason(null);
      } else {
        toast.error(
          resData?.message ||
            (editingReason
              ? "Failed to update Reason"
              : "Failed to add Reason"),
        );
      }
    } catch (error: any) {
      toast.error(
        error?.message ||
          (editingReason ? "Failed to update Reason" : "Failed to add Reason"),
      );
      logger.log(
        editingReason ? "Error updating Reason:" : "Error adding Reason:",
        error,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteUser = async (id: string) => {
    setIsDeleting(true);

    await deleteReason(id)
      .then((res) => {
        let data = res?.data;

        if (!!data?.status) {
          toast.success(data?.message || "Deleted successfully");
          setShowDeleteModal(false);
          mutate();
        } else {
          toast.error(data?.message);
        }
      })
      .catch((err: any) => {
        toast.error(err?.message);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  // const toggleUserStatus = (id: string) => {
  //   setReasons(
  //     reasons.map((user) =>
  //       user._id === id
  //         ? {
  //             ...user,
  //             status: user.status === "active" ? "inactive" : "active",
  //           }
  //         : user
  //     )
  //   );
  // };
  const filteredReasons =
    selectedType === "all"
      ? reasonData?.data || []
      : reasonData?.data?.filter((reason) => reason.type === selectedType) ||
        [];
  return (
    <>
      <div className="min-h-screen max-w-screen w-full ">
        <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <div className="bg-gray-100 rounded-full p-3">
                  <Users className="h-6 w-6 text-gray-700" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">
                    Stop Reasons
                  </h1>
                  {/* <p className="text-gray-700 mt-1">
                    Manage reasons for stopping streams and recordings
                  </p> */}
                </div>
              </div>
              <button
                onClick={() => {
                  setEditingReason(null);
                  setShowAddForm(true);
                  reset({
                    status: "active",
                    type: "",
                    reason: "",
                    message: "",
                  } as any);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 
   rounded-xl font-semibold transition-all duration-200 
   flex items-center space-x-2 shadow-sm"
              >
                <Plus className="h-5 w-5" />
                <span>Add Reason</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-gray-700 text-sm font-medium">
                    Total Reasons
                  </h3>
                  <p className="text-3xl font-bold text-gray-700 mt-2">
                    {reasonData?.data?.length}
                  </p>
                </div>
                <PlusCircleIcon className="h-8 w-8 text-blue-400" />
              </div>
            </div> */}
            <div
              onClick={() => setSelectedType("all")}
              className={`bg-white rounded-xl p-4 shadow-sm cursor-pointer transition-all ${
                selectedType === "all"
                  ? "ring-2 ring-blue-500"
                  : "hover:shadow-md"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-gray-700 text-sm font-medium">
                    Total Reasons
                  </h3>
                  <p className="text-3xl font-bold text-gray-700 mt-2">
                    {reasonData?.data?.length}
                  </p>
                </div>
                <PlusCircleIcon className="h-8 w-8 text-blue-400" />
              </div>
            </div>
            <div
              onClick={() => setSelectedType("streaming")}
              className={`bg-white rounded-xl p-4 shadow-sm overflow-hidden cursor-pointer transition-all ${
                selectedType === "streaming"
                  ? "ring-2 ring-green-500"
                  : "hover:shadow-md"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-gray-700 text-sm font-medium">
                    Stream Reasons
                  </h3>
                  <p className="text-3xl font-bold text-gray-700 mt-2">
                    {
                      reasonData?.data?.filter((u) => u?.type === "streaming")
                        .length
                    }
                  </p>
                </div>
                <div className="h-12 w-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Radio className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div
              onClick={() => setSelectedType("recording")}
              className={`bg-white rounded-xl p-4 shadow-sm overflow-hidden cursor-pointer transition-all ${
                selectedType === "recording"
                  ? "ring-2 ring-red-500"
                  : "hover:shadow-md"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-gray-700 text-sm font-medium">
                    Recording Reasons
                  </h3>
                  <p className="text-3xl font-bold text-gray-700 mt-2">
                    {
                      reasonData?.data?.filter((u) => u?.type === "recording")
                        .length
                    }
                  </p>
                </div>
                <div className="h-12 w-12 bg-red-500 rounded-full flex items-center justify-center">
                  <Video className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Reason List */}
          <div
            className="
          bg-white rounded-xl border border-gray-200 shadow-sm
           overflow-hidden"
          >
            <div className="p-4">
              <h2 className="text-xl font-semibold text-gray-800">
                List of Reasons
              </h2>
            </div>

            <div className="overflow-x-auto">
              {/* Desktop View */}
              <table className="table w-full">
                <thead className="bg-gray-50 border border-gray-100">
                  <tr>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Sr No
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Type
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Reason
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Message
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Status
                    </th>
                    <th className="text-left p-2 text-gray-800 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredReasons.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-16 text-center text-sm text-gray-500"
                      >
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <FileText className="h-12 w-12 text-gray-300" />
                          <h3 className="text-lg font-medium text-gray-600">
                            No Reasons Found
                          </h3>
                          <p className="text-xs text-gray-400">
                            No reasons have been added yet.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredReasons.map((reason: any, index: number) => (
                      <tr
                        key={reason._id}
                        className={`border-b border-gray-100 transition-colors ${
                          index % 2 === 0 ? "bg-white" : "bg-blue-50"
                        } hover:bg-gray-50`}
                      >
                        <td className="p-2 text-gray-700 font-normal">
                          {index + 1}
                        </td>

                        <td className="p-2 text-gray-700 font-normal capitalize">
                          {reason.type || "N/A"}
                        </td>

                        <td className="p-2 text-gray-700 font-normal">
                          {reason.reason?.length > 20
                            ? reason.reason.slice(0, 20) + "..."
                            : reason.reason || "N/A"}
                        </td>

                        <td className="p-2 text-gray-700 font-normal flex items-center">
                          <MessageSquare className="h-4 w-4 mr-2 text-gray-400" />
                          {reason.message?.length > 20
                            ? reason.message.slice(0, 20) + "..."
                            : reason.message || "N/A"}
                        </td>

                        <td className="p-2">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              reason.status === "active"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {reason.status || "N/A"}
                          </span>
                        </td>

                        <td className="p-2">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => {
                                setEditingReason(reason);
                                setShowAddForm(true);
                                reset({
                                  type: reason.type,
                                  reason: reason.reason?.trim(),
                                  message: reason.message?.trim(),
                                  status: (reason.status as any) || "active",
                                });
                              }}
                              className="text-blue-500 hover:text-gray-800 p-2 rounded-lg transition-colors"
                            >
                              <Edit className="h-4 w-4" />
                            </button>

                            <button
                              onClick={() => {
                                setDeleteId(reason?._id);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-500 hover:text-red-600 p-2 rounded-lg transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================================Add/Update Reason Modal========================== */}
          {showAddForm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white border border-gray-200 shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b  border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-gray-700">
                      {editingReason ? "Update Reason" : "Add New Reason"}
                    </h2>
                    <button
                      onClick={() => {
                        setShowAddForm(false);
                        setEditingReason(null);
                        reset({
                          status: "active",
                          type: "",
                          reason: "",
                          message: "",
                        } as any);
                      }}
                      className="text-gray-700/70 hover:text-gray-700 p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Type *
                    </label>
                    <select
                      {...register("type", { required: "Type is required" })}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg 
text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    >
                      <option value="" hidden className="bg-blue-900">
                        Select type
                      </option>
                      {/* <option value="Legal Advisor" className="bg-orange-500"> */}
                      <option value="streaming" className="bg-orange-500">
                        Streaming
                      </option>
                      {/* <option
                        value="Case Manager"
                        className="bg-white-900 text-black"
                      > */}
                      <option
                        value="recording"
                        className="bg-white-900 text-black"
                      >
                        Recording
                      </option>
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.type.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Reason *
                    </label>
                    <input
                      {...register("reason", {
                        required: "Reason is required",
                        minLength: {
                          value: 2,
                          message: "Reason must be at least 2 characters",
                        },
                        // pattern: {
                        //   value: /^[A-Za-z\s]+$/, // only letters and spaces
                        //   message: "Only alphabets are allowed",
                        // },
                        //   validate: (value) =>
                        //     value.trim().length > 0 ||
                        //     "Message cannot be empty or spaces only",
                        // })}
                        validate: (value) => {
                          if (value.startsWith(" ")) {
                            return "Starting space is not allowed";
                          }
                          if (value.endsWith(" ")) {
                            return "Ending space is not allowed";
                          }
                          return true;
                        },
                      })}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg 
text-gray-800 focus:outline-none focus:ring-2    placeholder-gray-400  focus:ring-blue-400 focus:border-transparent transition-all"
                      placeholder="Enter Reason"
                    />
                    {errors.reason && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.reason.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      {...register("message", {
                        required: "Message is required",
                        minLength: {
                          value: 2,
                          message: "Message must be at least 2 characters",
                        },
                        // pattern: {
                        //   value: /^[A-Za-z\s]+$/, // only letters and spaces
                        //   message: "Only alphabets are allowed",
                        // },
                        // validate: (value) =>
                        //   value.trim().length > 0 ||
                        //   "Message cannot be empty or spaces only",
                        validate: (value) => {
                          if (value.startsWith(" ")) {
                            return "Starting space is not allowed";
                          }
                          if (value.endsWith(" ")) {
                            return "Ending space is not allowed";
                          }
                          return true;
                        },
                      })}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg 
text-gray-800 focus:outline-none focus:ring-2  placeholder-gray-400 focus:ring-blue-400 focus:border-transparent transition-all"
                      placeholder="Enter Message"
                      rows={2}
                    />
                    {errors.message && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  {/* <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Status *
                    </label>
                    <select
                      {...register("status", {
                        required: "Status is required",
                      })}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg 
text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                    >
                      {/* <option value="" hidden className="bg-blue-900">
                        Select Status
                      </option> */}
                  {/* <option value="active" className="bg-blue-900">
                        Active
                      </option>
                      <option value="inactive" className="bg-blue-900">
                        Inactive
                      </option>
                    </select>
                    {errors.status && (
                      <p className="text-red-300 text-sm mt-1">
                        {errors.status.message}
                      </p>
                    )}
                  </div> */}
                </div>

                <div className="flex justify-end space-x-4 p-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingReason(null);
                      reset({
                        status: "active",
                        type: "",
                        reason: "",
                        message: "",
                      } as any);
                    }}
                    className="px-6 py-3 text-gray-700/70 hover:text-gray-700 border  border-gray-300 rounded-xl hover:bg-gray-100 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white
    rounded-xl transition-all font-medium flex items-center space-x-2 
    disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px] justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>
                          {editingReason ? "Updating..." : "Saving..."}
                        </span>
                      </>
                    ) : (
                      <>
                        <Save className="h-5 w-5" />
                        <span>
                          {editingReason ? "Update Reason" : "Add Reason"}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xs">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Delete Stop Reason?
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this Reason?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg border border-white/20 bg-red-500
    text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed
    min-w-[100px] flex items-center justify-center gap-2"
                disabled={isDeleting}
                onClick={() => {
                  if (deleteId) deleteUser(deleteId);
                }}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StopReasonsManager;
